import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api } from './api.js';
import { Login } from './screens/Login.js';
import { Today } from './screens/Today.js';
import { Wardrobe } from './screens/Wardrobe.js';
import { ItemDetail } from './screens/ItemDetail.js';
import { AddItem } from './screens/AddItem.js';
import { Categories } from './screens/Categories.js';
import { Laundry } from './screens/Laundry.js';
import { More } from './screens/More.js';
import { Occasions } from './screens/Occasions.js';
import { Outfits } from './screens/Outfits.js';
import { Trips } from './screens/Trips.js';
import { Loading } from './ui.js';

/**
 * Minimaler Hash-Router. Bewusst keine Router-Bibliothek: die App hat sechs Ansichten,
 * und jede zusätzliche Abhängigkeit ist auf einem selbst gehosteten Pi eine, die
 * irgendwann jemand aktualisieren muss.
 */
function useRoute(): [string, (to: string) => void] {
  const [route, setRoute] = useState(() => window.location.hash.slice(1) || '/heute');
  useEffect(() => {
    const onChange = (): void => setRoute(window.location.hash.slice(1) || '/heute');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const navigate = (to: string): void => {
    window.location.hash = to;
  };
  return [route, navigate];
}

/**
 * Vier Ziele, mehr passen nicht in Daumenreichweite. Die Symbole sind bewusst als
 * SVG-Pfade gesetzt statt als Emoji: Emoji rendern auf jedem System anders, haben
 * ihre eigene Farbe und lassen sich nicht auf den aktiven Zustand einfärben.
 */
const TABS: { path: string; label: string; icon: ReactNode }[] = [
  {
    path: '/heute',
    label: 'Heute',
    icon: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </>
    ),
  },
  {
    path: '/schrank',
    label: 'Schrank',
    icon: (
      <>
        <path d="M4 3h16v18H4z" />
        <path d="M12 3v18M9 11h.01M15 11h.01" />
      </>
    ),
  },
  {
    path: '/waesche',
    label: 'Wäsche',
    icon: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <circle cx="12" cy="13" r="4" />
        <path d="M8 6h.01" />
      </>
    ),
  },
  {
    path: '/mehr',
    label: 'Mehr',
    icon: (
      <>
        <circle cx="5" cy="12" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="19" cy="12" r="1.6" />
      </>
    ),
  },
];

export function App(): ReactNode {
  const [route, navigate] = useRoute();
  const [authed, setAuthed] = useState<boolean | null>(null);

  const status = useQuery({ queryKey: ['auth-status'], queryFn: api.authStatus, retry: 0 });

  // Ob eine Session gültig ist, sagt uns erst ein geschützter Endpunkt.
  useEffect(() => {
    if (status.data === undefined) return;
    void api
      .items({ category: '__probe__' })
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, [status.data]);

  if (status.isLoading || authed === null) return <Loading label="Wardrobe startet …" />;
  if (!authed) {
    return <Login configured={status.data?.configured ?? false} onSuccess={() => setAuthed(true)} />;
  }

  const itemMatch = /^\/schrank\/([0-9a-f-]{36})$/.exec(route);

  return (
    <div className="mx-auto max-w-screen-sm">
      {route === '/heute' && <Today navigate={navigate} />}
      {route === '/schrank' && <Wardrobe navigate={navigate} />}
      {route === '/schrank/neu' && <AddItem navigate={navigate} />}
      {itemMatch !== null && <ItemDetail id={itemMatch[1] as string} navigate={navigate} />}
      {route === '/waesche' && <Laundry />}
      {route === '/mehr' && <More navigate={navigate} onLogout={() => setAuthed(false)} />}
      {route === '/reisen' && <Trips navigate={navigate} />}
      {route === '/anlaesse' && <Occasions navigate={navigate} />}
      {route === '/kategorien' && <Categories navigate={navigate} />}
      {route === '/outfits' && <Outfits navigate={navigate} />}

      {/*
        Weicher Auslauf unter der Leiste. Ohne ihn scrollt der Inhalt sichtbar hinter
        das Milchglas und die Beschriftungen ueberlagern sich — auf Geraeten mit
        schwacher Unschaerfe besonders haesslich.
      */}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-20 h-32 bg-gradient-to-t from-[var(--color-ink)] via-[var(--color-ink)]/85 to-transparent"
        aria-hidden
      />

      {/*
        Schwebende Leiste statt angeklebter Kante: sie sitzt ueber dem Inhalt, nicht
        am Rand des Fensters. `bottom` rechnet die Home-Indicator-Leiste ein, deshalb
        braucht es hier kein zusaetzliches Padding — und deshalb sitzt sie schon beim
        ersten Aufbau richtig, ohne dass man erst scrollen muesste.
      */}
      <nav
        className="glass fixed inset-x-0 z-30 mx-auto flex max-w-[26rem] justify-around rounded-[1.6rem] px-2 py-1.5 ring-1 ring-white/10"
        style={{
          bottom: 'max(0.75rem, env(safe-area-inset-bottom))',
          left: 'max(0.75rem, env(safe-area-inset-left))',
          right: 'max(0.75rem, env(safe-area-inset-right))',
          boxShadow: '0 12px 40px -12px rgba(0,0,0,0.9)',
        }}
      >
        {TABS.map((tab) => {
          const active = route === tab.path || route.startsWith(`${tab.path}/`);
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`press relative flex flex-1 flex-col items-center gap-1 rounded-2xl py-1.5 text-[11px] font-medium ${
                active ? 'text-white' : 'text-white/45'
              }`}
              aria-current={active ? 'page' : undefined}
            >
              {active && (
                <span className="absolute inset-0 -z-10 rounded-2xl bg-white/10" aria-hidden />
              )}
              <svg
                viewBox="0 0 24 24"
                className="size-[22px]"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                {tab.icon}
              </svg>
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
